import type { ReactNode } from 'react';
import type { User } from '../../../types/auth';
import { useTheme } from '../../shared/theme/ThemeContext';
import { BRAND_GREEN } from '../../shared/theme/themeStyles';
import { accountTypeLabel as roleAccountLabel } from '../../../config/audience';

export function accountTypeLabel(user: User): string {
  if (user.is_system_guest) return 'Guest';
  return roleAccountLabel(user.role, user.sub_role);
}

function formatTimeLeftShort(ms: number): string {
  if (ms <= 0) return '0:00';
  const totalSec = Math.floor(ms / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

/** Shown beside timer values: budget is metered AI wall time, displayed as minutes:seconds (not clock time). */
const COMPUTE_BUDGET_UNIT = 'min:sec';

function BudgetCardShell({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  const { isDark } = useTheme();

  return (
    <div
      className={
        isDark
          ? 'rounded-lg border border-emerald-500/25 bg-gray-800/80 px-2.5 py-2'
          : 'rounded-lg border border-[#00854080] bg-white px-2.5 py-2 shadow-sm'
      }
    >
      <p
        className={
          isDark
            ? 'text-[9px] uppercase tracking-wide font-semibold text-emerald-400/90 mb-1'
            : 'text-[9px] uppercase tracking-wide font-semibold text-[#008540] mb-1'
        }
      >
        {title}
      </p>
      {children}
    </div>
  );
}

function BudgetProgress({ pct }: { pct: number }) {
  const { isDark } = useTheme();

  return (
    <div className={`h-1.5 rounded-full overflow-hidden mb-1.5 ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`}>
      <div
        className="h-full rounded-full transition-all duration-300"
        style={{
          width: `${pct}%`,
          background: isDark
            ? 'linear-gradient(90deg, #059669, #34d399)'
            : `linear-gradient(90deg, ${BRAND_GREEN}, #34d399)`,
        }}
      />
    </div>
  );
}

/** Remaining tool-time budget: guest via `guest_quota` only (per IP); signed-in users via user-id usage vs cap. */
export function UserComputeBudgetCard({ user }: { user: User }) {
  const { isDark } = useTheme();
  const muted = isDark ? 'text-gray-500' : 'text-gray-500';
  const valueClass = isDark ? 'font-mono text-sm font-semibold text-emerald-300 tabular-nums' : 'font-mono text-sm font-semibold text-[#008540] tabular-nums';

  if (user.is_system_guest && user.guest_quota) {
    const g = user.guest_quota;
    const pct = g.budget_ms > 0 ? Math.min(100, (g.used_ms / g.budget_ms) * 100) : 0;
    return (
      <BudgetCardShell title="Guest budget">
        <p className={`text-[10px] leading-tight mb-1.5 ${muted}`}>
          Analysis time · {COMPUTE_BUDGET_UNIT}
        </p>
        <BudgetProgress pct={pct} />
        <div className="flex justify-between items-center gap-2 text-[10px] leading-tight">
          <span className={muted}>Left ({COMPUTE_BUDGET_UNIT})</span>
          <span className={valueClass}>{formatTimeLeftShort(g.remaining_ms)}</span>
        </div>
        <p className={`text-[9px] mt-1 tabular-nums leading-tight ${muted}`}>
          {formatTimeLeftShort(g.used_ms)}/{formatTimeLeftShort(g.budget_ms)} ({COMPUTE_BUDGET_UNIT}) · IP
        </p>
      </BudgetCardShell>
    );
  }

  if (user.is_system_guest) return null;

  const mode = (user.compute_limit_mode || 'daily').toLowerCase();
  if (mode === 'unlimited') {
    return (
      <div
        className={
          isDark
            ? 'rounded-lg border border-emerald-500/25 bg-gray-800/80 px-2.5 py-2 flex items-center gap-2'
            : 'rounded-lg border border-[#00854080] bg-[#00854014] px-2.5 py-2 flex items-center gap-2'
        }
      >
        <i
          className={`ri-infinity-line text-base shrink-0 ${isDark ? 'text-emerald-400' : 'text-[#008540]'}`}
          aria-hidden
        />
        <p className={`text-xs font-medium leading-tight ${isDark ? 'text-emerald-300' : 'text-[#008540]'}`}>
          Unlimited analysis time
        </p>
      </div>
    );
  }

  const rem = user.compute_remaining_ms ?? 0;
  const usedDay = user.compute_used_ms_today ?? 0;
  const usedMo = user.compute_used_ms_month ?? 0;
  const capDay = user.compute_budget_ms_per_day;
  const capMo = user.compute_budget_ms_per_month;
  const isDaily = mode === 'daily';
  const cap = isDaily ? capDay : capMo;
  const used = isDaily ? usedDay : usedMo;
  const pct = cap != null && cap > 0 ? Math.min(100, (used / cap) * 100) : 0;

  return (
    <BudgetCardShell title={isDaily ? 'Daily' : 'Monthly'}>
      <p className={`text-[10px] leading-tight mb-1.5 ${muted}`}>
        Analysis time · {COMPUTE_BUDGET_UNIT}
      </p>
      <BudgetProgress pct={pct} />
      <div className="flex justify-between items-center gap-2 text-[10px] leading-tight">
        <span className={muted}>Left ({COMPUTE_BUDGET_UNIT})</span>
        <span className={valueClass}>{formatTimeLeftShort(rem)}</span>
      </div>
      <p className={`text-[9px] mt-1 tabular-nums leading-tight ${muted}`}>
        {formatTimeLeftShort(used)}/{cap != null ? formatTimeLeftShort(cap) : '—'} ({COMPUTE_BUDGET_UNIT}) ·{' '}
        {isDaily ? 'today' : 'month'} · per account
      </p>
    </BudgetCardShell>
  );
}
